import { decode } from '@msgpack/msgpack'
import { isDev, localApiKey } from '../lib/viteEnv'

export interface WsParams {
  sessionId?: string
  mode?: string
  transmission?: string
  character?: string
  story?: string
  features?: string
  /** Sentences spoken with the fast TTS model before switching to quality; '0' disables it. */
  fastSentences?: string
}

type MessageHandler = (msg: WsMessage) => void
type StatusHandler = (connected: boolean) => void

export interface WsMessage {
  type: string
  message?: string
  data?: unknown
}

export class WebSocketService {
  private ws: WebSocket | null = null
  private handlers = new Map<string, Set<MessageHandler>>()
  private statusHandlers = new Set<StatusHandler>()
  private params: WsParams = {}
  private stopped = false

  connect(params: WsParams) {
    this.params = params
    this.stopped = false
    this._connect()
  }

  disconnect() {
    this.stopped = true
    if (this.ws) {
      this.ws.onclose = null
      this.ws.close()
      this.ws = null
    }
  }

  send(msg: object) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg))
    }
  }

  on(type: string, handler: MessageHandler): () => void {
    if (!this.handlers.has(type)) this.handlers.set(type, new Set())
    this.handlers.get(type)!.add(handler)
    return () => this.handlers.get(type)?.delete(handler)
  }

  onStatusChange(cb: StatusHandler): () => void {
    this.statusHandlers.add(cb)
    return () => this.statusHandlers.delete(cb)
  }

  get isConnected() {
    return this.ws?.readyState === WebSocket.OPEN
  }

  private _connect() {
    if (this.stopped) return
    const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:'
    const wsParams = new URLSearchParams()
    if (this.params.sessionId) wsParams.set('session_id', this.params.sessionId)
    if (this.params.mode) wsParams.set('mode', this.params.mode)
    if (this.params.transmission) wsParams.set('transmission', this.params.transmission)
    if (this.params.character) wsParams.set('character', this.params.character)
    if (this.params.story) wsParams.set('story', this.params.story)
    if (this.params.features) wsParams.set('features', this.params.features)
    // Explicit undefined check rather than truthiness: '0' ("never use the fast
    // model") is truthy as a string today, but must survive if this ever becomes a number.
    if (this.params.fastSentences !== undefined) wsParams.set('fast_sentences', this.params.fastSentences)
    // In production the avataros_session cookie travels with the upgrade
    // request and authenticates the connection. In local dev the cookie
    // can't be used (HS256 vs JWKS — see docs/AUTH.md), so the dev key
    // gets spliced into the URL as ?api_key= — browsers can't set custom
    // headers on a WebSocket, so the query param is the only option.
    if (localApiKey) wsParams.set('api_key', localApiKey)
    const qs = wsParams.toString()
    const url = `${protocol}//${location.host}/ws${qs ? '?' + qs : ''}`

    this.ws = new WebSocket(url)

    this.ws.onopen = () => {
      this.statusHandlers.forEach(h => h(true))
    }

    this.ws.onclose = () => {
      this.statusHandlers.forEach(h => h(false))
      if (!this.stopped) setTimeout(() => this._connect(), 3000)
    }

    this.ws.onerror = (err) => {
      console.error('WebSocket error:', err)
    }

    this.ws.onmessage = async (event) => {
      try {
        let msg: WsMessage
        if (event.data instanceof Blob) {
          const ab = await event.data.arrayBuffer()
          msg = decode(new Uint8Array(ab)) as WsMessage
        } else if (event.data instanceof ArrayBuffer) {
          msg = decode(new Uint8Array(event.data)) as WsMessage
        } else {
          msg = JSON.parse(event.data) as WsMessage
        }
        this._dispatch(msg)
      } catch (e) {
        console.error('Failed to parse WS message:', e)
      }
    }
  }

  private _dispatch(msg: WsMessage) {
    const handlers = this.handlers.get(msg.type)
    if (handlers) handlers.forEach(h => h(msg))
    const all = this.handlers.get('*')
    if (all) all.forEach(h => h(msg))
  }
}

export const wsService = new WebSocketService()

// Dev-only: expose the singleton on globalThis so browser devtools can poke
// at the live WS (e.g. wsService.send({type:"kimodo:generate", data:{...}})).
// import.meta.env.DEV is statically false in production builds, so Vite
// tree-shakes this block out of the prod bundle — no leaked global surface.
if (isDev) {
  ;(globalThis as unknown as { wsService: WebSocketService }).wsService = wsService
}
